import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { evaluateCondition } from '@/utils/helpers';
import {
    cancelDynamicPricing,
    fetchDynamicPricing,
    updateOptions,
} from '@/actions/wizard/RequestWizardActions';
import Step from '@/views/Wizards/RequestWizard/Wrappers/Step';
import BudgetOptions from '../Inputs/Budget/BudgetOptions';
import BudgetInput from '../Inputs/Budget/BudgetInput';
import LivePricingOptions from '../Inputs/Budget/LivePricingOptions';

// Whether the model can price this request at all. Shared by the fetch effect
// and the render branch below so the two can never disagree about it.
const canPriceLive = (budgetTypes, request) =>
    Boolean(budgetTypes?.dynamicPricing) &&
    Boolean(request.postcode) &&
    Boolean(request.mealDate?.length) &&
    request.guests <= 40;

export default function BudgetStep() {
    const dispatch = useDispatch();
    const { requestWizard } = useSelector((state) => state);
    const { request, options } = requestWizard;

    const budgetTypes =
        options.budgetTypes.find((type) =>
            type.conditions?.every((condition) =>
                evaluateCondition(
                    condition.operator,
                    requestWizard[condition.dataSet][condition.key],
                    condition.value
                )
            )
        ) || options.budgetTypes.find((type) => type.default);

    const selectedOption =
        budgetTypes?.options?.find(
            (option) => option.value === request.budgetType
        ) || false;

    useEffect(() => {
        // Clearing matters as much as fetching: a stale snapshotId left behind
        // by a rule that is no longer model-priced would be linked to the quote
        // at submit and corrupt the measurement.
        if (!canPriceLive(budgetTypes, request)) {
            dispatch(cancelDynamicPricing());

            return;
        }

        // Set before the debounce, not inside the thunk: the cards must show a
        // placeholder from the moment we know a model price is coming, or the
        // hardcoded band flashes for the debounce window first.
        dispatch(updateOptions('dynamicPricingLoading', true));

        const timeout = setTimeout(() => dispatch(fetchDynamicPricing()), 300);
        return () => clearTimeout(timeout);
    }, [
        budgetTypes?.dynamicPricing,
        request.postcode,
        request.country,
        request.mealDate,
        request.guests,
        request.type,
        request.addressType,
        request.servingType,
    ]);

    // The model-priced budget types (the standard private-chef flow) get the
    // live pricing screen. Every other type -- meal prep, BBQ, kids party,
    // multi-day, full-time, cooking class, delivery platters -- has no model
    // behind it and keeps the band-and-budget step, as does a request the model
    // could not price: that screen has no budget field, and submit requires a
    // budget.
    const livePricing =
        canPriceLive(budgetTypes, request) && !options.dynamicPricingFailed;

    if (livePricing) {
        return (
            <Step>
                <h4 className="step-title">Your prices</h4>
                <p className="step-info wizard-live-pricing__subtitle">
                    Priced in real time for {request.guests} guests
                    {request.postcode ? ` · ${request.postcode}` : ''}
                </p>
                <LivePricingOptions />
            </Step>
        );
    }

    return (
        <Step>
            <h4 className="step-title">Pricing</h4>
            <p className="step-info">
                {budgetTypes?.description ||
                    `You'll see a range of chefs within your budget`}
            </p>
            <BudgetOptions
                budgetTypes={budgetTypes}
                selectedOption={selectedOption}
            />
            <BudgetInput
                budgetTypes={budgetTypes}
                selectedOption={selectedOption}
            />
        </Step>
    );
}
